import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, MessageSquare, Trash2, Pencil, AlertTriangle, Info } from "lucide-react";
import { formatEventDate } from "@/lib/dates";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const PROTECTED_MSG = "This event has booking history and must be kept for payment and booking records.";

const TEAM_STATUS = { requested: "Requested", quoted: "Quote received", accepted: "Accepted", paid: "Paid", completed: "Completed", cancelled: "Cancelled", declined: "Declined" };

const EXPENSE_CATEGORIES = ["Venue", "Catering", "Decor", "Rentals", "Entertainment", "Photography", "Attire", "Stationery", "Transportation", "Gifts", "Fees & permits", "Other"];
const usd = (n) => Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

function BudgetStat({ label, value, testId, negative }) {
  return (
    <div data-testid={testId} className="min-w-0">
      <div className="eyebrow">{label}</div>
      <div className={`font-serif text-xl sm:text-2xl mt-1 tabular-nums truncate ${negative ? "text-[#8A3B3B]" : ""}`}>
        {negative ? `−${usd(Math.abs(value))}` : usd(value)}
      </div>
    </div>
  );
}

function ExpenseDialog({ open, onOpenChange, initial, onSave, saving }) {
  const [f, setF] = useState({ name: "", category: "", amount: "", note: "" });
  useEffect(() => {
    if (open) setF({ name: initial?.name || "", category: initial?.category || "", amount: initial?.amount ?? "", note: initial?.note || "" });
  }, [open, initial]);
  const amount = Number(f.amount);
  const valid = f.name.trim() && f.category.trim() && f.amount !== "" && Number.isFinite(amount) && amount >= 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-dialog" className="max-w-md bg-[#FDFBF7]">
        <DialogHeader><DialogTitle className="font-serif text-3xl">{initial ? "Edit expense" : "Add expense"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label htmlFor="exp-name">Name</Label><Input id="exp-name" data-testid="expense-name-input" maxLength={120} value={f.name} onChange={ev => setF({ ...f, name: ev.target.value })} className="bg-white h-11 mt-1" placeholder="e.g. Table linens" /></div>
          <div>
            <Label htmlFor="exp-category">Category</Label>
            <Input id="exp-category" data-testid="expense-category-input" list="expense-categories" maxLength={60} value={f.category} onChange={ev => setF({ ...f, category: ev.target.value })} className="bg-white h-11 mt-1" placeholder="Choose or type a category" />
            <datalist id="expense-categories">{EXPENSE_CATEGORIES.map(c => <option key={c} value={c} />)}</datalist>
          </div>
          <div><Label htmlFor="exp-amount">Amount (USD)</Label><Input id="exp-amount" data-testid="expense-amount-input" type="number" min="0" step="0.01" value={f.amount} onChange={ev => setF({ ...f, amount: ev.target.value })} className="bg-white h-11 mt-1" placeholder="0.00" /></div>
          <div><Label htmlFor="exp-note">Note (optional)</Label><Textarea id="exp-note" data-testid="expense-note-input" maxLength={500} value={f.note} onChange={ev => setF({ ...f, note: ev.target.value })} className="bg-white mt-1" rows={2} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full h-11" onClick={() => onOpenChange(false)} data-testid="expense-cancel-btn">Cancel</Button>
          <Button data-testid="expense-save-btn" className="btn-sage rounded-full h-11" disabled={!valid || saving} onClick={() => onSave({ name: f.name.trim(), category: f.category.trim(), amount: Math.round(amount * 100) / 100, note: f.note.trim() })}>
            {saving ? "Saving…" : "Save expense"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BudgetPanel({ event, onSaved }) {
  const s = event.budget_summary;
  const expenses = event.manual_expenses || [];
  const [saving, setSaving] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [budgetDraft, setBudgetDraft] = useState("");
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const save = async (patch, okMsg) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/events/${event.event_id}`, { ...patch, budget_revision: event.budget_revision ?? 0 });
      onSaved(data);
      toast.success(okMsg);
      return true;
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || "Could not save budget";
      toast.error(msg);
      if (err.response?.status === 409) onSaved(null);
      return false;
    } finally { setSaving(false); }
  };
  const saveExpenses = (list, msg) => save({ manual_expenses: list.map(({ id, name, category, amount, note }) => ({ id, name, category, amount, note })) }, msg);

  if (!s) return null;
  const hasBudget = s.total_budget > 0;
  const pct = s.percent_used ?? 0;
  const over = s.remaining < 0;
  const barColor = over ? "bg-[#8A3B3B]" : pct >= 90 ? "bg-[#C98A2B]" : "bg-[color:var(--accent)]";
  const lineWarnings = (s.lines || []).filter(l => l.warning);

  return (
    <section data-testid="budget-panel" className="mt-8 card-elegant p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="eyebrow">Event budget</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-full h-9" data-testid="edit-budget-btn" onClick={() => { setBudgetDraft(String(s.total_budget)); setBudgetOpen(true); }}>
            <Pencil className="w-3.5 h-3.5 mr-2" /> Edit budget
          </Button>
          <Button size="sm" className="btn-sage rounded-full h-9" data-testid="add-expense-btn" onClick={() => { setEditing(null); setExpenseOpen(true); }}>
            <Plus className="w-3.5 h-3.5 mr-2" /> Add expense
          </Button>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <BudgetStat label="Total budget" value={s.total_budget} testId="budget-total" />
        <BudgetStat label="Booking commitments" value={s.booking_commitments} testId="budget-commitments" />
        <BudgetStat label="Manual expenses" value={s.manual_expenses_total} testId="budget-manual" />
        <BudgetStat label={over ? "Over budget" : "Remaining"} value={s.remaining} testId="budget-remaining" negative={over} />
      </div>

      <div className="mt-5">
        <div className="h-2 w-full rounded-full bg-[#EFEAE1] overflow-hidden" role="progressbar" aria-valuenow={hasBudget ? Math.min(100, Math.round(pct)) : 0} aria-valuemin={0} aria-valuemax={100} data-testid="budget-progress">
          <div className={`h-full rounded-full transition-[width] duration-500 ${barColor}`} style={{ width: `${hasBudget ? Math.min(100, pct) : 0}%` }} />
        </div>
        <div className="mt-2 text-xs text-[color:var(--text-muted)]" data-testid="budget-progress-label">
          {hasBudget
            ? over ? `${pct}% used · ${usd(Math.abs(s.remaining))} over budget` : `${pct}% of budget allocated`
            : "No budget set yet — add a total budget to track progress."}
          {s.cancelled_retained > 0 && ` · includes ${usd(s.cancelled_retained)} retained from cancelled bookings`}
        </div>
      </div>

      {(s.is_incomplete || s.is_estimated || s.warnings.length > 0) && (
        <div className="mt-4 space-y-2">
          {s.is_incomplete && (
            <div data-testid="budget-incomplete-warning" className="flex gap-2 text-sm text-[#8A3B3B] bg-[#FBF1F1] rounded-md px-3 py-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Calculation incomplete: some booking amounts are missing or inconsistent, so the totals above are not reliable.</span>
            </div>
          )}
          {s.is_estimated && !s.is_incomplete && (
            <div data-testid="budget-estimate-warning" className="flex gap-2 text-sm text-[#7A5A1E] bg-[#FBF4E4] rounded-md px-3 py-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <span>Some amounts are estimates or provisional and may change once bookings or refunds are finalized.</span>
            </div>
          )}
          {[...s.warnings.map(w => ({ key: w, text: w })), ...lineWarnings.map(l => ({ key: l.booking_id, text: `${l.vendor_name || "Booking"}: ${l.warning}`, id: l.booking_id }))].map(w => (
            <div key={w.key} className="text-xs text-[color:var(--text-muted)] pl-1" data-testid={w.id ? `budget-line-warning-${w.id}` : undefined}>
              {w.id ? <Link to={`/bookings/${w.id}`} className="link-underline">{w.text}</Link> : w.text}
            </div>
          ))}
        </div>
      )}

      <div className="mt-5">
        <div className="text-xs uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Manual expenses</div>
        {expenses.length === 0 ? (
          <div className="text-sm text-[#5C5C5C]" data-testid="no-expenses">No outside expenses added yet.</div>
        ) : (
          <ul className="divide-y divide-[#E8E2D9]" data-testid="expense-list">
            {expenses.map(x => (
              <li key={x.id} data-testid={`expense-row-${x.id}`} className="py-2.5 flex items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate" data-testid={`expense-name-${x.id}`}>{x.name}</div>
                  <div className="text-xs text-[color:var(--text-muted)] truncate">{x.category}{x.note ? ` · ${x.note}` : ""}</div>
                </div>
                <div className="text-sm tabular-nums shrink-0" data-testid={`expense-amount-${x.id}`}>{usd(x.amount)}</div>
                <div className="flex gap-1 shrink-0">
                  <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Edit expense" data-testid={`edit-expense-${x.id}`} onClick={() => { setEditing(x); setExpenseOpen(true); }}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-[#8A3B3B]" aria-label="Delete expense" data-testid={`delete-expense-${x.id}`} onClick={() => setDeleting(x)}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={budgetOpen} onOpenChange={setBudgetOpen}>
        <DialogContent data-testid="budget-dialog" className="max-w-sm bg-[#FDFBF7]">
          <DialogHeader><DialogTitle className="font-serif text-3xl">Edit budget</DialogTitle></DialogHeader>
          <Label htmlFor="budget-input">Total event budget (USD)</Label>
          <Input id="budget-input" data-testid="budget-input" type="number" min="0" step="1" value={budgetDraft} onChange={ev => setBudgetDraft(ev.target.value)} className="bg-white h-11" />
          <DialogFooter>
            <Button variant="outline" className="rounded-full h-11" onClick={() => setBudgetOpen(false)} data-testid="budget-cancel-btn">Cancel</Button>
            <Button data-testid="budget-save-btn" className="btn-sage rounded-full h-11" disabled={saving || budgetDraft === "" || !(Number(budgetDraft) >= 0)}
              onClick={async () => { if (await save({ budget: Math.round(Number(budgetDraft) * 100) / 100 }, "Budget updated")) setBudgetOpen(false); }}>
              {saving ? "Saving…" : "Save budget"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExpenseDialog open={expenseOpen} onOpenChange={setExpenseOpen} initial={editing} saving={saving}
        onSave={async (vals) => {
          const list = editing ? expenses.map(x => (x.id === editing.id ? { ...x, ...vals } : x)) : [...expenses, vals];
          if (await saveExpenses(list, editing ? "Expense updated" : "Expense added")) setExpenseOpen(false);
        }} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent data-testid="delete-expense-dialog" className="bg-[#FDFBF7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete “{deleting?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>This removes the expense from your budget. Bookings are not affected.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-expense-cancel" className="rounded-full h-11">Keep</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-expense-confirm" disabled={saving} className="rounded-full h-11 bg-[#8A3B3B] hover:bg-[#6F2E2E] text-white"
              onClick={async () => { if (await saveExpenses(expenses.filter(x => x.id !== deleting.id), "Expense deleted")) setDeleting(null); }}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

export default function EventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [e, setE] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const venueNeeded = !!e?.needs_venue && !e?.location;
  const [q, setQ] = useState("");

  const load = () => api.get(`/events/${id}`).then(({ data }) => setE(data));
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (!open) return;
    api.get("/vendors", { params: { q, limit: 40 } }).then(({ data }) => setVendors(data));
  }, [open, q]);

  const add = async (vendor_id) => {
    try {
      await api.post(`/events/${id}/add-vendor`, { vendor_id });
      toast.success("Vendor added to event");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const canDelete = e?.can_delete === true;
  const blockReason = e?.delete_block_reason || PROTECTED_MSG;
  const retainedCount = (e?.bookings || []).length;
  const remove = async () => {
    setDeleting(true);
    try {
      await api.delete(`/events/${id}`);
      toast.success(`"${e.title}" was deleted`);
      nav("/planner/dashboard");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not delete event");
      setConfirmDelete(false);
      load();
    } finally { setDeleting(false); }
  };

  if (!e) return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24">Loading…</div>;

  return (
    <div data-testid="event-detail-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-12">
      <Link to="/planner/dashboard" className="text-sm link-underline text-[#5C5C5C]">← Back to dashboard</Link>

      <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="eyebrow mb-3" data-testid="event-date-label">{formatEventDate(e.date)}</div>
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl leading-none break-words">{e.title}</h1>
          <div className="mt-4 text-base sm:text-lg text-[#5C5C5C]" data-testid="event-meta-line">
            {venueNeeded
              ? <span className="text-[color:var(--accent)] font-medium" data-testid="event-venue-needed">Venue needed</span>
              : <span data-testid="event-location-text">{e.location || "Location TBD"}</span>}
            {" "}· {e.guest_count} guests · ${e.budget} budget
          </div>
          {venueNeeded && (
            <Button asChild variant="outline" className="rounded-full h-10 mt-3" data-testid="find-venue-btn">
              <Link to={`/browse?event=${e.event_id}&category=venue`}>
                <Search className="w-4 h-4 mr-2" /> Find a venue
              </Link>
            </Button>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2 self-start sm:self-auto shrink-0">
          <Button asChild variant="outline" className="rounded-full h-11 px-6">
            <Link to={`/browse?event=${e.event_id}`} data-testid="find-vendors-for-event-btn">
              <Search className="w-4 h-4 mr-2" /> Find vendors for this event
            </Link>
          </Button>
          <Button data-testid="add-vendor-btn" className="btn-sage rounded-full h-11 px-6" onClick={() => setOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> Add vendor
          </Button>
          <Button
            data-testid="delete-event-btn"
            variant="outline"
            className="rounded-full h-11 px-5 border-[#E8E2D9] text-[#8A3B3B] hover:bg-[#FBF1F1]"
            onClick={() => (canDelete ? setConfirmDelete(true) : toast.error(blockReason))}
            aria-disabled={!canDelete}
            title={canDelete ? "Delete this event" : blockReason}
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete event
          </Button>
        </div>
      </div>
      {!canDelete && (
        <div className="mt-3 text-xs text-[color:var(--text-muted)]" data-testid="event-protected-note">{blockReason}</div>
      )}

      {e.budget_summary && (
        <BudgetPanel event={e} onSaved={(data) => {
          if (!data) return load();
          setE(prev => ({ ...prev, budget: data.budget, manual_expenses: data.manual_expenses, budget_revision: data.budget_revision, budget_summary: data.budget_summary }));
        }} />
      )}

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="delete-event-dialog" className="bg-[#FDFBF7]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete “{e.title}”?</AlertDialogTitle>
            <AlertDialogDescription data-testid="delete-event-description">
              {retainedCount > 0
                ? `This event will be removed from your dashboard. Its ${retainedCount === 1 ? "cancelled or declined booking request and its messages are" : `${retainedCount} cancelled or declined booking requests and their messages are`} retained for the vendor and for support records.`
                : "This event has no bookings. It will be removed from your dashboard."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-event-cancel" className="rounded-full h-11">Keep event</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-event-confirm" onClick={remove} disabled={deleting} className="rounded-full h-11 bg-[#8A3B3B] hover:bg-[#6F2E2E] text-white">
              {deleting ? "Deleting…" : "Delete event"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {e.notes && <p className="mt-6 max-w-2xl text-[#5C5C5C] leading-relaxed">{e.notes}</p>}

      <section className="mt-12 sm:mt-16">
        <div className="eyebrow mb-6">Your team</div>
        {e.vendors.length === 0 ? (
          <div className="text-[#5C5C5C]" data-testid="no-team-vendors">No vendors added yet.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {e.vendors.map(v => {
              const active = (e.bookings || []).filter(b => b.vendor_id === v.vendor_id && !["cancelled", "declined"].includes(b.status));
              const bk = active[0] || (e.bookings || []).find(b => b.vendor_id === v.vendor_id);
              return (
              <div key={v.vendor_id} data-testid={`team-vendor-${v.vendor_id}`} className="card-elegant overflow-hidden flex flex-col">
                <Link to={`/vendor/${v.vendor_id}?event=${e.event_id}`} data-testid={`team-vendor-profile-${v.vendor_id}`}>
                  {v.cover_image && <img src={v.cover_image} className="w-full aspect-[16/10] object-cover" alt="" />}
                  <div className="p-5 pb-3">
                    <div className="eyebrow">{v.category}</div>
                    <div className="font-serif text-2xl mt-1">{v.name}</div>
                  </div>
                </Link>
                {bk && (
                  <div className="px-5 pb-5 mt-auto space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <span data-testid={`team-booking-status-${v.vendor_id}`} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#F5F2EB] text-[#1A1A1A]">{TEAM_STATUS[bk.status] || bk.status}</span>
                      {Number(bk.quote_amount) > 0 && <span className="text-sm text-[#5C5C5C]">${Number(bk.quote_amount).toLocaleString()}</span>}
                    </div>
                    <Link to={`/bookings/${bk.booking_id}`} data-testid={`team-view-booking-${v.vendor_id}`}>
                      <Button className="btn-sage rounded-full w-full h-10 text-sm"><MessageSquare className="w-4 h-4 mr-2" /> View booking & messages</Button>
                    </Link>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </section>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#FDFBF7] max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-3xl">Add a vendor</DialogTitle></DialogHeader>
          <Input placeholder="Search vendors" data-testid="add-vendor-search" value={q} onChange={ev => setQ(ev.target.value)} className="bg-white h-11" />
          <div className="space-y-2 mt-4">
            {vendors.map(v => (
              <div key={v.vendor_id} className="flex items-center justify-between p-3 border border-[#E8E2D9] rounded-md bg-white">
                <div>
                  <div className="eyebrow">{v.category} · {v.city}</div>
                  <div className="font-serif text-lg">{v.name}</div>
                </div>
                <Button data-testid={`add-vendor-${v.vendor_id}`} onClick={() => add(v.vendor_id)} className="btn-sage rounded-full">Add</Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
